import imageCompression from "browser-image-compression";

// 상세 페이지는 이미지를 전체 폭으로 보여주므로 가로가 이 값 이상이어야 선명하다.
const DETAIL_WIDTH = 1024;
// 목록 썸네일은 정사각형으로 크롭(object-cover)하므로 짧은 변이 이 값 이상이어야 선명하다.
const THUMB_MIN = 512;

/**
 * 원본 이미지의 크기(EXIF 회전 반영)를 반환합니다.
 */
async function getImageSize(
  file: File
): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const { width, height } = bitmap;
  bitmap.close();
  return { width, height };
}

/**
 * 이미지 파일 목록(FileList)을 받아 리사이징 및 압축 후,
 * File 객체 배열을 Promise로 반환합니다.
 *
 * 이미지는 상세(전체 폭)와 목록(정사각형 크롭) 두 방식으로 표시되므로, 축소하더라도
 * 상세용 가로(DETAIL_WIDTH)와 썸네일용 짧은 변(THUMB_MIN)이 모두 확보되도록 더 완만한
 * 쪽에 맞춰 축소합니다. 이미 충분히 작은 이미지는 압축이 해상도만 깎아 흐려지므로
 * 원본을 그대로 사용합니다.
 * @param {FileList} imageFiles - <input type="file">에서 얻은 원본 파일 목록
 * @returns {Promise<File[] | undefined>} 리사이징된 File 객체의 배열 또는 에러 발생 시 undefined
 */
export default async function compressImages(
  imageFiles: FileList
): Promise<File[] | undefined> {
  try {
    const compressionPromises = Array.from(imageFiles).map(async (file) => {
      const { width, height } = await getImageSize(file);
      const shortSide = Math.min(width, height);
      const longSide = Math.max(width, height);

      // 가로 축소 여지 / 짧은 변 축소 여지 중 더 완만한(1에 가까운) 쪽을 택해
      // 두 표시 방식 모두에서 해상도가 붕괴하지 않게 한다. 업스케일은 하지 않는다.
      const widthScale = width > DETAIL_WIDTH ? DETAIL_WIDTH / width : 1;
      const shortScale = shortSide > THUMB_MIN ? THUMB_MIN / shortSide : 1;
      const scale = Math.max(widthScale, shortScale);

      // 축소가 불필요하면 압축이 해상도만 깎으므로 원본을 그대로 사용
      if (scale >= 1) {
        return file;
      }

      const maxWidthOrHeight = Math.round(longSide * scale);

      return imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight,
        useWebWorker: true,
      });
    });

    const compressedFiles = await Promise.all(compressionPromises);

    return compressedFiles;
  } catch (error) {
    console.error("이미지 리사이징 실패:", error);
    return undefined;
  }
}
